import { useCallback, useState } from "react";
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import moment, { type Moment } from "moment-timezone";
import { toast } from "sonner";
import { omit } from "lodash";
import { useScheduleContext } from "../context/useScheduleContext";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useDefaultSection } from "./useDefaultSection";
import { usePermissions } from "../usePermissions";
import { getOrderedWeekdays } from "../../helpers/getOrderedWeekdays";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useIsTemplate } from "../useIsTemplate";
import useApi from "../../../../../utils/api";
import { usePayCodesMap } from "../../../../Settings/PayPolicies/CalculationGroup/Form/hooks/usePayCodeMap";
import { useRegularPayCode } from "../../../../Settings/PayPolicies/CalculationGroup/Form/hooks/useRegularPayCode";
import { useCompany } from "../../../../../utils/hooks/company";
import { useUser } from "../../../../../utils/hooks/user";
import { getFormFormattedValues } from "../../helpers/form/getFormFormattedValues";
import { getUnassignedShiftFormattedValues } from "../../helpers/form/getUnassignedShiftFormattedValues";
import { calculateFormValues } from "../../helpers/form/calculateFormValues";
import { getDefaultSectionForUser } from "../../helpers/form/getDefaultSectionForUser";
import { unassignedShiftsResource } from "../../helpers/data/unassignedShiftsResource";
import { combineDateAndTime } from "../../../../../utils/helpers/date";
import { withLoadingToast } from "../../helpers/withLoadingToast";
import { getResourceUserId } from "../../helpers/data/groupedResourceId";
import { weekdays, type Weekday } from "../../../../../components/shared/inputs/WeekdaySelect/types";
import type { TransferResult } from "../../components/modals/Transfer/ResponseTable";
import type { Resource } from "../../types/Resource";
import type { SelectedCellOrEvent } from "../../types/ScheduleState";
import type { Event } from "../../types/Event";

interface FormActionsProps {
    openDeleteModal: ({ type, event, onSuccess }: { type: string, event: Event | SelectedCellOrEvent, onSuccess?: () => void }) => void;
    close: () => void;
    form: UseFormReturn<any>;
}

interface SuccessResponseProps {
    isNew?: boolean;
    status?: string;
    isTemplate?: boolean;
    moreModal?: boolean;
    startDate: Moment | string;
    endDate: Moment | string;
    date?: string;
    user: any;
    silent?: boolean;
}

export const getApiErrorMessage = (error: any, fallback: string) => {
    if (typeof error === "string") return error;
    const response = error?.response?.data;
    if (response?.errors && typeof response.errors === "object") {
        return Object.values(response.errors).map(String).join(", ");
    }
    if (Array.isArray(response?.fields)) {
        return response.fields.map((field: any) => field?.message).filter(Boolean).join(", ");
    }
    return response?.message || fallback;
};

const toTemplateDay = (date: any): Weekday | undefined => {
    const lowered = String(date).toLowerCase();
    if ((weekdays as readonly string[]).includes(lowered)) {
        return lowered as Weekday;
    }
    return moment(date).format("dddd").toLowerCase() as Weekday;
};

const getActionType = (isNew: boolean, mode: string, moreModal?: boolean) => {
    if (moreModal) {
        return isNew
            ? "create-more-modal-event"
            : "update-more-modal-event";
    }

    if (mode === "month") {
        return isNew
            ? "create-calendar-event"
            : "update-calendar-event";
    }

    return isNew
        ? "create-event"
        : "update-event";
};

export const getDefaultPayCode = (user: any, payCodesMap: Record<string, any> | null, regularPayCode: any) => {
    if (!payCodesMap) return regularPayCode;

    const employeeTypeCode = user?.employeeType?.code;
    const employeeTypeAsDefault = user?.calculationGroup?.useEmployeeTypeAsDefaultPayCode;
    const regularDefaultPayCode = user?.calculationGroup?.regularDefaultPayCode;

    if (employeeTypeAsDefault && employeeTypeCode && employeeTypeCode in payCodesMap) {
        return payCodesMap[employeeTypeCode];
    }

    if (regularDefaultPayCode?.code && regularDefaultPayCode.code in payCodesMap) {
        return payCodesMap[regularDefaultPayCode.code];
    }

    return regularPayCode;
};

export const useFormActions = ({ form, close, openDeleteModal }: FormActionsProps) => {
    const { t } = useTranslation();
    const userTimezone = moment.tz.guess(true);
    const company = useCompany();
    const [{ mode, template, day }] = useFilters();
    const { defaultSection } = useDefaultSection();
    const payCodesMap = usePayCodesMap();
    const regularPayCode = useRegularPayCode(payCodesMap);
    const { authPost, authPut } = useApi();
    const { allocatedGroups } = useGroups();
    const currentUser = useUser();
    const {
        selectedCellOrEvent,
        type,
        dispatch,
        clearSelectedEvents,
        unassignedEvents,
        resetCount,
        templates,
        refetchEventStatistics
    } = useScheduleContext();
    const isTemplate = useIsTemplate();
    const { canDelete, canDeleteUnassignedShift } = usePermissions();
    const { data: ipData } = useQuery({
        queryKey: ["https://geolocation-db.com/json/"],
        queryFn: ({ queryKey: [url] }) => {
            return axios.get(url);
        },
    });

    const orderedWeekdays = getOrderedWeekdays();
    const [results, setResults] = useState<TransferResult[] | null>(null);

    const setFormErrorsFromApi = useCallback((error: any) => {
        const response = error?.response?.data;

        if (typeof error === "string") {
            toast.error(error);
            return;
        }

        // Case 1: { errors: { field: message } }
        if (response?.errors && typeof response.errors === "object") {
            Object.entries(response.errors).forEach(([field, message], index) => {
                form.setError(
                    field,
                    { type: "server", message: String(message) },
                    { shouldFocus: index === 0 }
                );
            });
            return;
        }

        // Case 2: { fields: [{ field, message }] }
        if (Array.isArray(response?.fields)) {
            response.fields.forEach((err: any, index: number) => {
                form.setError(
                    err.field,
                    { type: "server", message: err.message },
                    { shouldFocus: index === 0 }
                );
            });
            return;
        }

        // Fallback
        toast.error(response?.message || t("something-went-wrong"));
    }, [form, t]);

    const onSuccess = useCallback((response: SuccessResponseProps) => {
        const isNew = !!response?.isNew;
        const status = response?.status;
        if (response?.silent) {
            // multi-user save: the results modal reports the outcome
        } else if (response?.isTemplate) {
            const message = isNew
                ? t("shift-template-created-successfully")
                : t("shift-template-updated-successfully")
            toast.success(message);
        } else if (status === "approved") {
            const message = isNew
                ? t("event-published-successfully")
                : t("event-updated-and-published")
            toast.success(message);
        } else {
            const message = isNew
                ? t("event-created-successfully")
                : t("event-updated-successfully")
            toast.success(message);
        }
        const type = getActionType(isNew, mode, response?.moreModal);
        const startDate = moment.parseZone(response.startDate).tz(userTimezone, true);
        let endDate = moment.parseZone(response.endDate).tz(userTimezone, true);
        if (moment(endDate).isBefore(moment(startDate), "seconds")) {
            endDate = moment(endDate).add(1, "day");
        }

        dispatch({
            type,
            payload: {
                ...response,
                startDate,
                endDate,
                resourceId: typeof response.user === "string" ? response.user : response.user?.id,
            }
        });
        if (isNew) {
            resetCount?.();
            refetchEventStatistics();
        }
        if (!response?.silent) {
            clearSelectedEvents();
            close?.();
        }
    }, [mode, userTimezone, dispatch, clearSelectedEvents, close, t, resetCount, refetchEventStatistics]);

    const saveEventMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async (values: any) => {
            //TODO:  edit event => edit all events based on recurrence (does not repeat) or only the current event we have selected
            const isNew = !selectedCellOrEvent?.id ||
                (selectedCellOrEvent?.id || "").includes("event_") ||
                (selectedCellOrEvent?.id || "").includes("unassigned-shift_");
            const method = isNew ? authPost : authPut;
            const base = type === "team" ? "/schedules/v2" : "/my/schedules/v2";
            const url = isNew ? base : `${base}/${selectedCellOrEvent.id}`;

            const response = await method(url, { data: values?.values });
            return {
                ...response,
                isNew,
                user: values?.user || response?.user,
                moreModal: selectedCellOrEvent?.moreModal,
                silent: values?.silent
            };
        },
        onSuccess,
        onError: (error, variables) => {
            if (!variables?.silent) setFormErrorsFromApi(error);
        },
    });

    const saveForUsers = useCallback(async (values: any, extra: Record<string, any> = {}) => {
        const users: any[] = values.users || [];
        const silent = users.length > 1;
        const payloads = users.map((user: any) => {
            const defaultPayCode = getDefaultPayCode(user, payCodesMap, regularPayCode);
            const _defaultSection = getDefaultSectionForUser({
                user,
                date: selectedCellOrEvent?.date,
                company,
                allocatedGroups,
                defaultPayCode
            });

            const formValues = calculateFormValues(
                values,
                { ...selectedCellOrEvent, user },
                _defaultSection
            );

            const formattedValues = getFormFormattedValues(formValues);
            return {
                values: {
                    ...formattedValues,
                    ...extra,
                    user: getResourceUserId(user.id),
                },
                user,
                silent,
            };
        });

        const settled = await Promise.allSettled(
            payloads.map((payload) => saveEventMutation.mutateAsync(payload))
        );

        if (!silent) return;

        setResults(settled.map((result, index) => {
            const { values: submitted, user } = payloads[index];
            return result.status === "fulfilled"
                ? {
                    status: true,
                    schedule: { ...submitted, ...result.value },
                    user,
                }
                : {
                    status: false,
                    message: getApiErrorMessage(result.reason, t("something-went-wrong")),
                    schedule: submitted,
                    user,
                };
        }));
    }, [
        payCodesMap,
        regularPayCode,
        selectedCellOrEvent,
        company,
        allocatedGroups,
        saveEventMutation,
        t,
    ]);

    const closeResults = useCallback(() => {
        const anySucceeded = results?.some((result) => result.status);
        setResults(null);
        // Keep the drawer open when every save failed so the form can be fixed and retried
        if (anySucceeded) {
            clearSelectedEvents();
            close?.();
        }
    }, [results, clearSelectedEvents, close]);

    const saveTemplateMutattion = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async (values: any) => {
            const isNew = !isTemplate || !selectedCellOrEvent?.id || (selectedCellOrEvent?.id || "").includes("event_");
            const base = ["team", "template", "profile"].includes(type)
                ? `/shift-templates/v2/${values.templateId}`
                : `/my/shift-templates/v2/${values.templateId}`;
            const url = isNew ? base : `${base}/${selectedCellOrEvent.id}`;
            const data = (mode === "day" && isTemplate) ? { ...values, date: day } : values;
            const method = isNew ? authPost : authPut;
            const response = await method(url, { data: omit(data, ["templateId"]) });
            return {
                ...response,
                isNew,
                isTemplate
            }
        },
        onSuccess: (response: SuccessResponseProps) => {
            clearSelectedEvents();
            if (response.isTemplate) {
                const date = mode === "day"
                    ? moment().format("YYYY-MM-DD")
                    : moment().weekday(orderedWeekdays.indexOf(response?.date as unknown as Weekday)).format("YYYY-MM-DD");
                const startDate = combineDateAndTime(moment(date), moment.parseZone(response.startDate));
                const endDate = combineDateAndTime(moment(date), moment.parseZone(response.endDate));

                onSuccess({
                    ...response,
                    // date,
                    startDate,
                    endDate
                });
            } else {
                toast.success(t("shift-template-created-successfully"));
                close?.();
            }
        },
        onError: (error) => setFormErrorsFromApi(error),
    });

    const createRequestMutation = useMutation({
        ...withLoadingToast(t("sending-request")),
        mutationFn: (values: any) => authPost("/request", { data: values }),
        onSuccess: () => {
            clearSelectedEvents();
            toast.success(t("request-created-successfully"));
            close();
        },
        onError: (error: any) => {
            const message =
                error?.response?.status === 403
                    ? t("request-not-allowed")
                    : t("request-creation-failed");

            toast.error(message);
        },
    });

    const unassigendShiftSubmitMutation = useMutation({
        ...withLoadingToast(t("saving")),
        mutationFn: async (values: any) => {
            const isNew = selectedCellOrEvent?.isCreating;
            const method = isNew ? authPost : authPut;
            const url = isNew ? "/unassigned-shifts" : `/unassigned-shifts/${selectedCellOrEvent?.id}`;
            const response = await method(url, { data: omit(values, ["user"]) });
            return { ...response, isNew };
        },
        onSuccess: (response: any) => {
            const message = response?.isNew
                ? t("event-created-successfully")
                : t("event-updated-successfully")
            toast.success(message);
            const exist = unassignedEvents?.find((event: any) => event.id === response?.id);
            const type = (response?.isNew && !exist) ? "create-unassigned-event" : "update-unassigned-event";

            dispatch({
                type,
                payload: {
                    ...response,
                    startDate: moment.parseZone(response.startDate).tz(userTimezone, true),
                    endDate: moment.parseZone(response.endDate).tz(userTimezone, true),
                    resourceId: unassignedShiftsResource.id
                }
            });
            close();
        },
        onError: (error) => setFormErrorsFromApi(error),
    });

    const saveDraft = useCallback(async () => {
        const values = form.getValues();
        const isValid = await form.trigger();
        if (isValid) {
            if (mode === "month") {
                await saveForUsers(values);
            } else {
                const formValues = calculateFormValues(values, selectedCellOrEvent, defaultSection);
                const formattedValues = getFormFormattedValues(formValues);
                saveEventMutation.mutate({ values: formattedValues });
            }
        }
    }, [
        form,
        mode,
        selectedCellOrEvent,
        defaultSection,
        saveEventMutation,
        saveForUsers,
    ]);

    const publish = useCallback(async () => {
        const values = form.getValues();
        const isValid = await form.trigger();
        if (isValid) {
            if (mode === "month") {
                await saveForUsers(values, { status: "approved" });
            } else {
                const formValues = calculateFormValues(values, selectedCellOrEvent, defaultSection);
                const formattedValues = getFormFormattedValues(formValues);
                saveEventMutation.mutate({ values: { ...formattedValues, status: "approved" } });
            }
        }
    }, [
        form,
        mode,
        selectedCellOrEvent,
        defaultSection,
        saveEventMutation,
        saveForUsers,
    ]);

    const saveTemplate = useCallback(async (_templateId: string | undefined) => {
        const values = form.getValues();
        const isValid = await form.trigger();
        const templateId = isTemplate ? template?.id : _templateId;

        if (!isValid) {
            toast.error(t("fix-errors-before-saving-as-template"));
            return false;
        }

        if (mode === "month") {
            const users = values.users;
            const results = await Promise.allSettled(
                users.map((user: any) => {
                    const defaultPayCode = getDefaultPayCode(user, payCodesMap, regularPayCode);
                    const _defaultSection = getDefaultSectionForUser({
                        user,
                        date: selectedCellOrEvent?.date,
                        company,
                        allocatedGroups,
                        defaultPayCode
                    });

                    const formValues = calculateFormValues(
                        values,
                        { ...selectedCellOrEvent, user },
                        _defaultSection
                    );

                    const formattedValues = getFormFormattedValues(formValues);
                    return saveTemplateMutattion.mutateAsync({
                        ...formattedValues,
                        date: toTemplateDay(formattedValues.date),
                        user: getResourceUserId(user.id),
                        templateId: templateId || templates?.[0]?.id,
                    });
                })
            );

            // The mutation's onError already reported each failure, so we only
            // need to tell the caller not to treat this as a successful save.
            return results.every((result) => result.status === "fulfilled");
        }

        try {
            const formValues = calculateFormValues(values, selectedCellOrEvent, defaultSection);
            const formattedValues = getFormFormattedValues(formValues);
            await saveTemplateMutattion.mutateAsync({
                ...formattedValues,
                date: toTemplateDay(formattedValues.date),
                templateId: templateId || templates?.[0]?.id,
            });
        } catch {
            return false;
        }

        return true;
    }, [
        form,
        template?.id,
        mode,
        payCodesMap,
        regularPayCode,
        selectedCellOrEvent,
        company,
        allocatedGroups,
        defaultSection,
        saveTemplateMutattion,
        templates,
        isTemplate,
        t
    ]);

    const unassignedShiftSubmit = useCallback(async () => {
        const values = form.getValues();
        const formValues = calculateFormValues(values, selectedCellOrEvent, defaultSection);
        const data = getFormFormattedValues(formValues);
        const unassigendShiftFormattedValues = getUnassignedShiftFormattedValues(data, values?.slots);
        const isValid = await form.trigger();
        if (isValid) {
            unassigendShiftSubmitMutation.mutate(unassigendShiftFormattedValues);
        }
    }, [form, selectedCellOrEvent, defaultSection, unassigendShiftSubmitMutation]);

    const createRequest = useCallback(async () => {
        const values = form.getValues();
        const isValid = await form.trigger();
        if (isValid) {
            if (mode === "month") {
                const users = values.users;
                await Promise.allSettled(
                    users.map((user: any) => {
                        const defaultPayCode = getDefaultPayCode(user, payCodesMap, regularPayCode);
                        const _defaultSection = getDefaultSectionForUser({
                            user,
                            date: selectedCellOrEvent?.date,
                            company,
                            allocatedGroups,
                            defaultPayCode
                        });
                        const formValues = calculateFormValues(values, selectedCellOrEvent, _defaultSection);
                        const data = getFormFormattedValues(formValues);
                        const request = {
                            user: getResourceUserId(user?.id),
                            type: "edit-schedule",
                            device: "web-application",
                            ip: ipData?.data?.IPv4 ?? "",
                            data: { ...data, id: selectedCellOrEvent?.id },
                            namespace: "schedule",
                        }
                        return createRequestMutation.mutateAsync(request);
                    }));
            } else {
                const formValues = calculateFormValues(values, selectedCellOrEvent, defaultSection);
                const data = getFormFormattedValues(formValues);
                const request = {
                    user: getResourceUserId((selectedCellOrEvent?.user as Resource)?.id) ?? currentUser?.id,
                    type: "edit-schedule",
                    device: "web-application",
                    ip: ipData?.data?.IPv4 ?? "",
                    data: { ...data, id: selectedCellOrEvent?.id },
                    namespace: "schedule",
                }
                // onError surfaces the failure as a toast; swallow the rejection
                // so it doesn't escape the click handler as an unhandled rejection.
                await createRequestMutation.mutateAsync(request).catch(() => undefined);
            }
        }
    }, [
        form,
        mode,
        payCodesMap,
        regularPayCode,
        selectedCellOrEvent,
        company,
        allocatedGroups,
        createRequestMutation,
        defaultSection,
        ipData,
        currentUser?.id
    ]);

    const remove = useCallback(() => {
        if (!selectedCellOrEvent?.isCreating && selectedCellOrEvent?.isUnassignedShift) {
            if (canDeleteUnassignedShift) {
                openDeleteModal({
                    type: "cell-unassign",
                    event: selectedCellOrEvent,
                    onSuccess: close
                });
            } else {
                close();
            }
        } else if (!selectedCellOrEvent?.isCreating && canDelete) {
            openDeleteModal({
                type: "cell",
                event: selectedCellOrEvent as SelectedCellOrEvent,
                onSuccess: close
            });
        } else {
            close();
        }
    }, [selectedCellOrEvent, canDelete, canDeleteUnassignedShift, openDeleteModal, close]);

    return {
        publish,
        saveTemplate,
        saveDraft,
        remove,
        createRequest,
        unassignedShiftSubmit,
        results,
        closeResults,
        loading: saveEventMutation.isPending ||
            saveTemplateMutattion.isPending ||
            createRequestMutation.isPending ||
            unassigendShiftSubmitMutation.isPending
    }
}
